'use client'

import {
    useState,
    HTMLInputTypeAttribute,
    ReactNode,
    useImperativeHandle,
    Ref,
} from 'react'

import useDebouncedValue from '../../hooks/debounced-value'
import useUpdateEffect from '../../hooks/update-effect'
import { CheckIcon, Cross2Icon } from '@radix-ui/react-icons'
import { IconButton, TextField } from '@radix-ui/themes'
import { TextFieldRootUnsetWhitespace } from '../../primitives/styled/text-fields'

type TProps = Omit<TextField.RootProps, 'value' | 'onChange' | 'defaultValue' | 'type'> & {
    ref?: Ref<TTextInputRef>
    defaultValue?: string
    onValueChange?: (value?: string) => void
    /** Any input type is accepted for compatibility; Radix renders the textual ones (text, number, date...). */
    type?: HTMLInputTypeAttribute
    icon?: ReactNode
    placeholder?: string
    debounce?: number
    allowClear?: boolean
    onConfirm?: (value: string) => void | Promise<void>
}

export type TTextInputRef = {
    clear: () => void
    hasValue: () => boolean
    setValue: (value: string | number) => void
}

const TextInput = ({
    ref,
    defaultValue,
    onValueChange,
    type,
    icon,
    placeholder,
    debounce,
    allowClear,
    onConfirm,
    ...rest
}: TProps) => {
    const [inputValue, setInputValue] = useState<string | undefined>(defaultValue)

    const [confirmation, setConfirmation] = useState(false)
    const [lastConfirmedValue, setLastConfirmedValue] = useState<string | undefined>(defaultValue)

    useImperativeHandle(
        ref,
        () => ({
            clear: () => setInputValue(''),
            hasValue: () => inputValue != '' && inputValue !== undefined,
            setValue: value => setInputValue(value.toString()),
        }),
        [inputValue]
    )

    const debouncedValue = useDebouncedValue(inputValue, debounce ?? 100)

    // initial value is not announced as a change.
    useUpdateEffect(() => {
        onValueChange?.(debouncedValue)
    }, [debouncedValue])

    return (
        <TextFieldRootUnsetWhitespace
            {...rest}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            type={type as TextField.RootProps['type']}
            placeholder={placeholder}
        >
            {icon && <TextField.Slot>{icon}</TextField.Slot>}

            {onConfirm && lastConfirmedValue != inputValue && (
                <TextField.Slot side="right">
                    <IconButton
                        disabled={confirmation}
                        variant="classic"
                        color="grass"
                        onClick={async () => {
                            setConfirmation(true)
                            try {
                                await onConfirm(inputValue ?? '')
                                setLastConfirmedValue(inputValue)
                            }
                            catch (e) {
                                // the value stays unconfirmed, so the user can retry
                                console.error(e)
                            }
                            finally {
                                setConfirmation(false)
                            }
                        }}
                    >
                        <CheckIcon />
                    </IconButton>
                </TextField.Slot>
            )}
            {allowClear && (
                <TextField.Slot side="right">
                    <IconButton variant="ghost" onClick={() => setInputValue('')}>
                        <Cross2Icon />
                    </IconButton>
                </TextField.Slot>
            )}
        </TextFieldRootUnsetWhitespace>
    )
}

export default TextInput
